import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { connect } from "react-redux";
import * as Router from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import * as yup from "yup";
import { DashboardLayout } from "component/layout/dashboard";
import { DescribedRadioGroup } from "component/shared/described-radio-group";
import type { DescribedRadioOption } from "component/shared/described-radio-group";
import { PageHeader } from "component/shared/page-header";
import { SettingsSection } from "component/shared/settings-page";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { User } from "model/user";
import { saveUser } from "redux/slice/authentication";
import { RootState } from "redux/store";
import { darkModeDev } from "shared/env";
import { PageProps } from "shared/page-props";
import { validate } from "shared/yup";
import { DarkMode } from "type/dark-mode";
import { displayPermitted, isV10Display, showIdxCode, showPageHead } from "./access";
import { CodeField } from "./code-field";
import { PrimaryColorDropdown } from "./primary-color-dropdown";
import { PrimaryColorPicker } from "./primary-color-picker";

// Copy per the Display frames: VPLAT 10 (`2551:14077`) and CSS 1 (`3241:9568`) for v10, VPLAT 32
// (`2985:23184`) and CSS 2 (`3242:9810`) for v9. The page description names only the sections the
// client actually sees.
const COPY = {
	pageDescriptionV10:
		"Configure your website's primary color and theme, including light mode, dark mode, and auto-detect.",
	pageDescriptionV10Code: "Configure your website's appearance — primary color, theme, and custom code overrides.",
	// Build correction: the v9 frames' descriptions mention theme, which v9 doesn't have.
	pageDescriptionV9: "Configure your website's primary color.",
	pageDescriptionV9Code: "Configure your website's appearance — primary color and custom code overrides.",
	customCode:
		"Add additional CSS, HTML, or scripts to further customize your website. Feel free to skip it if you are not comfortable with code.",
	idxStylesheet:
		"Add custom CSS to style IDX content on your website — including search results, listing pages, and contact forms. IDX content is rendered inside a shadow DOM, so standard CSS from your site's stylesheet won't apply here. No need to wrap your code in <style> tags.",
	idxContent:
		"Add scripts — like a Google Analytics tracking snippet — external stylesheet links, or CDN references to your IDX content area. Content added here must include the appropriate HTML tags, such as <script> or <style>.",
	pageHead:
		"Add CSS, scripts, and other HTML to the head of your page. This is optional — skip it if you're not comfortable with code. Content added here must include the appropriate HTML tags, such as <script> or <style>.",
	primaryColor:
		"Sets the color used throughout your website's search, listings, and forms — including buttons, links, and interactive elements.",
};

// Listed in the frames' order.
const THEME_OPTIONS: DescribedRadioOption[] = [
	{
		value: DarkMode.LIGHT.id,
		label: DarkMode.LIGHT.label,
		description: "Always display your IDX content with a light background and dark text.",
	},
	{
		value: DarkMode.DARK.id,
		label: DarkMode.DARK.label,
		description: "Always display your IDX content with a dark background and light text.",
	},
	{
		value: DarkMode.AUTO.id,
		label: DarkMode.AUTO.label,
		// VPLAT 10's copy. CSS panel 1 redraws this page with "matches the visitor's system or
		// browser theme preference", but Kestrel decides from the host page's background colour
		// (kestrel use-dark-mode), which is what this sentence says.
		description: "Automatically shows a light or dark background to match the page your visitors are viewing.",
	},
];

// Shown while a v10 client has no colour stored. Storing it as the real default is SPFF-6, which
// changes what Kestrel renders, so showing it here must not save it.
const DEFAULT_PRIMARY_COLOR = "#9E9E9E";

interface Props
	extends PageProps,
		Router.RouteComponentProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {
	user: User;
}

interface State {
	colorScheme: string;
	darkMode: number;
	shadowDomCss: string;
	shadowDomHtml: string;
	cssOverride: string;
	saving: boolean;
	saveSuccess: boolean;
}

const mapStateToProps = (state: RootState) => {
	return {
		authLoading: state.authentication.loading,
		authError: state.authentication.error,
	};
};

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			saveUser,
		},
		dispatch
	);

// MAX-4419: the display half of the old Version & Platform page (/settings/version), plus the custom
// code fields that were /settings/css.
class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = {
			...this.stateFromUser(props.user),
			saving: false,
			saveSuccess: false,
		};
	}

	public componentDidUpdate(prevProps: Props) {
		const { authLoading, authError, user } = this.props;
		if (this.state.saving && prevProps.authLoading && !authLoading) {
			// On success, re-baseline on what the server saved; on failure, keep the edits.
			if (authError) {
				this.setState({ saving: false, saveSuccess: false });
			} else {
				this.setState({ ...this.stateFromUser(user), saving: false, saveSuccess: true });
			}
		}
	}

	private stateFromUser(user: User) {
		return {
			colorScheme: user.colorScheme || "",
			darkMode: this.effectiveDarkMode(user),
			shadowDomCss: user.shadowDomCss || "",
			shadowDomHtml: user.shadowDomHtml || "",
			cssOverride: user.cssOverride || "",
		};
	}

	// Kestrel renders an unset darkMode as auto-detect (kestrel app/base/mui), so show that rather
	// than no selection. Also the dirty baseline, so the pre-selection isn't an unsaved change.
	private effectiveDarkMode(user: User): number {
		return user.darkMode || DarkMode.AUTO.id;
	}

	private validationSchema = {
		colorScheme: yup
			.string()
			.matches(/^#([a-f0-9]{3}){1,2}$/i, { excludeEmptyString: true, message: "Enter a hex color, e.g. #9E9E9E" }),
	};

	// MAX-4577: the colour is checked only once the client changes it. A stored value the check would
	// refuse (one saved through the old v10 field) otherwise blocked every save on the page, custom
	// code included, with nothing shown on the v9 dropdown.
	private errors() {
		const colorChanged = this.state.colorScheme !== (this.props.user.colorScheme || "");
		return colorChanged ? validate(this.validationSchema, this.state) : undefined;
	}
	
	private isV10(): boolean {
		return isV10Display(this.props.user);
	}

	private showTheme(): boolean {
		// v9 has no theme: darkMode only reaches Kestrel. The dev flag gated the old dark mode control
		// and is on in every env.
		return this.isV10() && darkModeDev;
	}

	private showIdxCode(): boolean {
		return showIdxCode(this.props.user);
	}

	private showPageHead(): boolean {
		return showPageHead(this.props.user);
	}

	private isDirty = (): boolean => {
		const { user } = this.props;
		const { colorScheme, darkMode, shadowDomCss, shadowDomHtml, cssOverride } = this.state;
		return (
			colorScheme !== (user.colorScheme || "") ||
			darkMode !== this.effectiveDarkMode(user) ||
			shadowDomCss !== (user.shadowDomCss || "") ||
			shadowDomHtml !== (user.shadowDomHtml || "") ||
			cssOverride !== (user.cssOverride || "")
		);
	};

	private handleSave = () => {
		const { saveUser, user } = this.props;
		const { colorScheme, darkMode, shadowDomCss, shadowDomHtml, cssOverride } = this.state;
		if (this.errors()) {
			return;
		}
		// Only write darkMode when the Theme section is shown and the user changed it. The Auto-detect
		// pre-selection for an unset value is display only, and v9 has no Theme control at all.
		const themeChanged = this.showTheme() && darkMode !== this.effectiveDarkMode(user);
		// Code fields the client can't see here pass through unchanged, the same way.
		saveUser({
			user: {
				...user,
				colorScheme,
				darkMode: themeChanged ? darkMode : user.darkMode,
				...(this.showIdxCode() ? { shadowDomCss, shadowDomHtml } : {}),
				...(this.showPageHead() ? { cssOverride } : {}),
			},
		});
		this.setState({ saving: true, saveSuccess: false });
	};

	private handleCancel = () => {
		this.setState(this.stateFromUser(this.props.user));
	};

	public render() {
		const { user, authError, history } = this.props;
		const { colorScheme, darkMode, shadowDomCss, shadowDomHtml, cssOverride, saving, saveSuccess } = this.state;
		const errors = this.errors();
		const isV10 = this.isV10();
		const idxCode = this.showIdxCode();
		const pageHead = this.showPageHead();
		const title = "Display";
		const pageDescription = isV10
			? idxCode ? COPY.pageDescriptionV10Code : COPY.pageDescriptionV10
			: idxCode || pageHead ? COPY.pageDescriptionV9Code : COPY.pageDescriptionV9;

		const primaryColor = (
			<>
				{isV10 ? (
					<PrimaryColorPicker
						id="display-primary-color"
						label="Primary Color"
						value={colorScheme}
						defaultValue={DEFAULT_PRIMARY_COLOR}
						error={errors && errors.colorScheme ? errors.colorScheme : undefined}
						onChange={(colorScheme) => this.setState({ colorScheme })}
					/>
				) : (
					<PrimaryColorDropdown
						id="display-primary-color"
						label="Primary Color"
						value={colorScheme}
						error={errors && errors.colorScheme ? errors.colorScheme : undefined}
						onChange={(colorScheme) => this.setState({ colorScheme })}
					/>
				)}
			</>
		);

		const theme = (
			<DescribedRadioGroup
				name="display-theme"
				aria-labelledby="display-theme-title"
				value={darkMode}
				options={THEME_OPTIONS}
				onChange={(value) => this.setState({ darkMode: value as number })}
			/>
		);

		return (
			<DashboardLayout
				permitted={user && displayPermitted(user)}
				title={title}
				header={
					<PageHeader
						title={title}
						description={pageDescription}
						action={
							user.baseUrl && (
								<Mui.Link href={user.baseUrl} target="_blank" rel="noopener noreferrer">
									View Website <Icons.ExternalLink size={16} />
								</Mui.Link>
							)
						}
					/>
				}
			>
				<SettingsSection refreshed title="Primary Color" description={COPY.primaryColor} fieldColumn>
					{primaryColor}
				</SettingsSection>
				{this.showTheme() && (
					<SettingsSection refreshed title="Theme" titleId="display-theme-title">
						{theme}
					</SettingsSection>
				)}
				{idxCode && (
					<SettingsSection refreshed title="Custom Code (Advanced)" description={COPY.customCode}>
						<CodeField
							title="IDX Stylesheet"
							description={COPY.idxStylesheet}
							value={shadowDomCss}
							onChange={(shadowDomCss) => this.setState({ shadowDomCss })}
						/>
						<CodeField
							title="IDX Content"
							description={COPY.idxContent}
							value={shadowDomHtml}
							onChange={(shadowDomHtml) => this.setState({ shadowDomHtml })}
							divider
						/>
					</SettingsSection>
				)}
				{pageHead && (
					<SettingsSection refreshed title="Page Head (Advanced)" description={COPY.pageHead}>
						<CodeField
							value={cssOverride}
							onChange={(cssOverride) => this.setState({ cssOverride })}
						/>
					</SettingsSection>
				)}

				<SettingsStickyFooter
					refreshed
					dirty={this.isDirty()}
					saving={saving}
					authError={authError}
					saveSuccess={saveSuccess}
					onSave={this.handleSave}
					onCancel={this.handleCancel}
					onSaveFeedbackClose={() => this.setState({ saveSuccess: false })}
				/>

				<UnsavedChangesPrompt isDirty={this.isDirty} history={history} />
			</DashboardLayout>
		);
	}
}

export const SettingsDisplayPage = connect(mapStateToProps, mapDispatchToProps)(Component);
